import cx from 'classnames'
import Styles from './TikTokPostSettings.module.scss'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ScheduledPostTarget } from 'types'
import { observer } from 'mobx-react-lite'
import { Select } from 'components/Select/Select'
import { Item } from '@react-stately/collections'
import { InformationCircleContained, Lock3, UserProfileAdd, UsersProfiles1 } from 'blaze-ui/icons'
import Globe from 'blaze-ui/icons/20/Globe'
import {
  TikTokPrivacyLevel,
  useTikTokCreatorInfo,
} from 'hooks/queries/externalIntegrations/tiktok/useTikTokCreatorInfo'
import { Checkbox } from 'blaze-ui/Checkbox/Checkbox'
import { Text } from 'blaze-ui/Text/Text'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { Pressable } from '@react-aria/interactions'
import { useUpdateScheduledPost } from 'hooks/queries/scheduledPosts/useUpdateScheduledPost'
import { useUpdateScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useUpdateScheduledPostTarget'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { isTikTokSettings } from 'components/ArticlePostPreview/validators/tiktok'

interface Props {
  scheduledPost: ScheduledPostShow
  scheduledPostTarget?: ScheduledPostTarget
  channel: DesignChannel
  isModal?: boolean
}

const privacyLevelMap: Record<TikTokPrivacyLevel, string> = {
  [TikTokPrivacyLevel.Public]: 'Everyone',
  [TikTokPrivacyLevel.Mutuals]: 'Followers you follow back',
  [TikTokPrivacyLevel.Followers]: 'Only your followers',
  [TikTokPrivacyLevel.Private]: 'Only you',
}

const privacyLevelIcon: Record<TikTokPrivacyLevel, JSX.Element> = {
  [TikTokPrivacyLevel.Public]: <Globe />,
  [TikTokPrivacyLevel.Mutuals]: <UsersProfiles1 />,
  [TikTokPrivacyLevel.Followers]: <UserProfileAdd />,
  [TikTokPrivacyLevel.Private]: <Lock3 />,
}

const CheckboxSetting = ({
  label,
  isChecked,
  isDisabled,
  onChange,
}: {
  label: string
  isChecked: boolean
  isDisabled: boolean
  onChange: () => void
}) => (
  <Pressable onPress={onChange}>
    <div className={cx(Styles.toggle, { [Styles.disabled]: isDisabled })}>
      <Checkbox color="var(--selected-fill)" isDisabled={isDisabled} isSelected={isChecked} />
      <Text variant="primary">{label}</Text>
    </div>
  </Pressable>
)

const BrandAndMusicUsageConfirmation = () => (
  <span className={Styles.description}>
    By posting, you agree to TikTok's{' '}
    <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noreferrer">
      Branded Content Policy
    </a>{' '}
    and{' '}
    <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer">
      Music Usage Confirmation
    </a>
    .
  </span>
)

const MusicUsageConfirmation = () => (
  <span className={Styles.description}>
    By posting, you agree to TikTok's{' '}
    <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer">
      Music Usage Confirmation
    </a>
    .
  </span>
)

export const TikTokPostSettings = observer(({ scheduledPost, scheduledPostTarget, channel, isModal }: Props) => {
  const updateScheduledPost = useUpdateScheduledPost({ suppressToast: true })
  const updateScheduledPostTarget = useUpdateScheduledPostTarget()

  const isTargetMode = !!scheduledPostTarget

  const currentSettings = isTargetMode
    ? scheduledPostTarget.target_overrides?.post_settings
    : scheduledPost.post_settings

  const { data: creatorInfo, isLoading } = useTikTokCreatorInfo({
    externalIntegrationId: isTargetMode
      ? scheduledPostTarget.external_integration_id
      : scheduledPost.external_integration?.id,
  })
  if (channel !== DesignChannel.TikTokPost) return null

  if (isLoading || !creatorInfo || creatorInfo?.error.code !== 'ok') {
    return null
  }

  const {
    privacy_level_options: userPrivacyLevels,
    comment_disabled: userCommentsDisabled,
    duet_disabled: userDuetDisabled,
    stitch_disabled: userStitchDisabled,
  } = creatorInfo.data

  const defaultSettings = {
    privacy_level: TikTokPrivacyLevel.Public,
    disable_comment: false,
    disable_duet: false,
    disable_stitch: false,
    disclose_content: false,
    brand_organic_toggle: false,
    brand_content_toggle: false,
  }

  const updateSettings = (updatedSettings: any) => {
    if (isTargetMode) {
      updateScheduledPostTarget.mutate({
        scheduled_post_id: scheduledPostTarget.scheduled_post_id,
        target_id: scheduledPostTarget.id,
        target_overrides: {
          ...scheduledPostTarget.target_overrides,
          post_settings: updatedSettings,
        },
      })
    } else {
      updateScheduledPost.mutate({
        id: scheduledPost.id,
        params: { postSettings: updatedSettings },
      })
    }
  }

  if (!currentSettings && !updateScheduledPost.isLoading && !updateScheduledPostTarget.isLoading) {
    updateSettings(defaultSettings)
    return null
  }

  if (!isTikTokSettings(currentSettings)) {
    return null
  }

  const userPrivacyLevel = currentSettings.privacy_level as TikTokPrivacyLevel | undefined
  const allowComments = currentSettings.disable_comment === false
  const allowDuet = currentSettings.disable_duet === false
  const allowStitch = currentSettings.disable_stitch === false
  const brandOrganic = !!currentSettings.brand_organic_toggle
  const brandContent = !!currentSettings.brand_content_toggle
  const discloseContent = !!currentSettings.disclose_content
  const disableBrandContent = userPrivacyLevel === TikTokPrivacyLevel.Private

  const handleUpdateSetting = (key: string, value: string | boolean) => {
    updateSettings({ ...currentSettings, [key]: value })
  }

  const handleDiscloseContent = () => {
    const params = discloseContent
      ? { disclose_content: false, brand_content_toggle: false, brand_organic_toggle: false }
      : { disclose_content: true }

    updateSettings({ ...currentSettings, ...params })
  }

  const discloseContentMessage = () => {
    if (brandContent)
      return `Your video will be labeled as 'Paid partnership'. This cannot be changed once your video is posted.`
    if (brandOrganic)
      return `Your video will be labeled as 'Promotional content'. This cannot be changed once your video is posted.`
  }

  const disabledKeyTooltip = (key: TikTokPrivacyLevel) => {
    switch (key) {
      case TikTokPrivacyLevel.Private:
        return 'Branded content visibility cannot be set to private.'
      default:
        return ''
    }
  }

  const disabledKeys = brandContent ? [TikTokPrivacyLevel.Private] : []

  return (
    <div className={cx(Styles.postSettingsContainer, { [Styles.modal]: isModal })}>
      <div className={Styles.section}>
        <span className={Styles.headerLabel}>Who can view this post</span>
        <div className={Styles.privacyOptions}>
          <Select
            variant="secondary"
            selectedKey={userPrivacyLevel}
            onSelectionChange={(key) => handleUpdateSetting('privacy_level', key as TikTokPrivacyLevel)}
            useFullWidth
            useButtonWidth
            aria-label="Privacy selection"
            placeholder="Select who can view your post"
            disabledKeys={disabledKeys}
          >
            {userPrivacyLevels.map((key) => (
              <Item key={key} textValue={key}>
                <Tooltip hide={!disabledKeys.includes(key)} label={disabledKeyTooltip(key)}>
                  <div className={Styles.privacyItem}>
                    {privacyLevelIcon[key]}
                    {privacyLevelMap[key]}
                  </div>
                </Tooltip>
              </Item>
            ))}
          </Select>
        </div>
      </div>
      <div className={Styles.section}>
        <span className={Styles.headerLabel}>Allow people to</span>
        <div className={Styles.toggleContainer}>
          <CheckboxSetting
            label="Comment"
            isChecked={allowComments}
            isDisabled={userCommentsDisabled}
            onChange={() => handleUpdateSetting('disable_comment', allowComments)}
          />
          <CheckboxSetting
            label="Duet"
            isChecked={allowDuet}
            isDisabled={userDuetDisabled}
            onChange={() => handleUpdateSetting('disable_duet', allowDuet)}
          />
          <CheckboxSetting
            label="Stitch"
            isChecked={allowStitch}
            isDisabled={userStitchDisabled}
            onChange={() => handleUpdateSetting('disable_stitch', allowStitch)}
          />
        </div>
      </div>
      <div className={Styles.section}>
        <Pressable onPress={handleDiscloseContent}>
          <div className={Styles.toggleRow}>
            <Text variant="smallList">Disclose video content</Text>
            <ToggleButton name="discloseContent" size="sm-md" checked={discloseContent} handleChange={() => {}} />
          </div>
        </Pressable>
        {!discloseContent && (
          <span className={Styles.description}>
            Turn on to disclose that this video promotes goods or services in exchange for something of value. Your
            video could promote yourself, a third party, or both.
          </span>
        )}
        {discloseContent && (brandContent || brandOrganic) && (
          <div className={Styles.discloseContentNotice}>
            <span>
              <InformationCircleContained size={20} color="var(--selected-stroke)" />
            </span>
            <span className={Styles.discloseContentText}>{discloseContentMessage()}</span>
          </div>
        )}
      </div>
      {discloseContent && (
        <>
          <div className={Styles.section}>
            <Pressable onPress={() => handleUpdateSetting('brand_organic_toggle', !brandOrganic)}>
              <div className={Styles.toggleRow}>
                <Text variant="smallList">Your brand</Text>
                <ToggleButton name="discloseContent" size="sm-md" checked={brandOrganic} handleChange={() => {}} />
              </div>
            </Pressable>
            <span className={Styles.description}>
              You are promoting yourself or your own business. This video will be classified as Brand Organic.
            </span>
          </div>
          <Tooltip hide={!disableBrandContent} label="Branded content visibility cannot be set to private.">
            <div className={cx(Styles.section, { [Styles.disabled]: disableBrandContent })}>
              <Pressable
                isDisabled={disableBrandContent}
                onPress={() => handleUpdateSetting('brand_content_toggle', !brandContent)}
              >
                <div className={Styles.toggleRow}>
                  <Text variant="smallList">Branded content</Text>
                  <ToggleButton name="discloseContent" size="sm-md" checked={brandContent} handleChange={() => {}} />
                </div>
              </Pressable>
              <span className={Styles.description}>
                You are promoting another brand or a third party. This video will be classified as Branded Content.
              </span>
            </div>
          </Tooltip>
          {brandContent && <BrandAndMusicUsageConfirmation />}
        </>
      )}
      {!brandContent && <MusicUsageConfirmation />}
    </div>
  )
})
